import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import type { ClientPreferences, MediaContactPreference, SpokespersonPreference, TonePreset } from '@/types/preferences';
import { trackPreferenceMetric } from '@/lib/preferencesAnalytics';

const STYLE_NOTES_LIMIT = 6;

const toIsoString = (value: unknown): string => {
  try {
    if (typeof value === 'string' && value.trim()) {
      const parsed = new Date(value);
      if (!Number.isNaN(parsed.getTime())) return parsed.toISOString();
    }
    if (value && typeof (value as any).toDate === 'function') {
      const d = (value as any).toDate();
      if (d instanceof Date && !Number.isNaN(d.getTime())) return d.toISOString();
    }
    if (value && typeof (value as any).seconds === 'number') {
      const nanos = typeof (value as any).nanoseconds === 'number' ? (value as any).nanoseconds : 0;
      const d = new Date((value as any).seconds * 1000 + Math.floor(nanos / 1e6));
      if (!Number.isNaN(d.getTime())) return d.toISOString();
    }
  } catch {}
  return new Date().toISOString();
};

const stripUndefined = <T,>(value: T): T => {
  if (Array.isArray(value)) {
    return value.map((item) => stripUndefined(item)).filter((item) => item !== undefined) as unknown as T;
  }
  if (value && typeof value === 'object') {
    const next: Record<string, unknown> = {};
    Object.entries(value as Record<string, unknown>).forEach(([key, val]) => {
      const cleaned = stripUndefined(val as unknown);
      if (cleaned !== undefined) next[key] = cleaned;
    });
    return next as T;
  }
  return (value === undefined ? undefined : value) as T;
};

const normalizeStyleNotes = (notes?: string[] | null): string[] | undefined => {
  if (!Array.isArray(notes)) return undefined;
  const cleaned = notes
    .map((note) => (typeof note === 'string' ? note.trim() : ''))
    .filter(Boolean);
  if (!cleaned.length) return undefined;
  return Array.from(new Set(cleaned)).slice(0, STYLE_NOTES_LIMIT);
};

const normalizeTerms = (terms?: string[] | null): string[] | undefined => {
  if (!Array.isArray(terms)) return undefined;
  const cleaned = terms
    .map((term) => (typeof term === 'string' ? term.trim() : ''))
    .filter(Boolean);
  if (!cleaned.length) return undefined;
  return Array.from(new Set(cleaned)).slice(0, 24);
};

export const normalizeClientPreferences = (raw?: Record<string, unknown> | null): ClientPreferences | null => {
  if (!raw || typeof raw !== 'object') return null;
  const version = typeof raw.version === 'number' && Number.isFinite(raw.version) ? raw.version : 1;
  const locale = raw.locale === 'ar' || raw.locale === 'en' ? raw.locale : undefined;
  const spokespersonRaw = raw.spokesperson as Record<string, unknown> | undefined;
  const mediaRaw = raw.mediaContact as Record<string, unknown> | undefined;
  const tone = typeof raw.tone === 'string' && raw.tone.trim() ? raw.tone.trim() : undefined;
  const styleNotes = normalizeStyleNotes(raw.styleNotes as string[] | undefined);
  const lengthPreference = typeof raw.lengthPreference === 'string' && raw.lengthPreference.trim()
    ? raw.lengthPreference.trim()
    : undefined;
  const structurePreference = typeof raw.structurePreference === 'string' && raw.structurePreference.trim()
    ? raw.structurePreference.trim()
    : undefined;
  const forbiddenTerms = normalizeTerms(raw.forbiddenTerms as string[] | undefined);
  const requiredTerms = normalizeTerms(raw.requiredTerms as string[] | undefined);
  const confidence = typeof raw.confidence === 'number' ? raw.confidence : undefined;
  const source = typeof raw.source === 'string' && raw.source.trim() ? raw.source.trim() : undefined;

  const spokesperson: SpokespersonPreference | undefined =
    spokespersonRaw && typeof spokespersonRaw.name === 'string' && spokespersonRaw.name.trim()
      ? {
          name: spokespersonRaw.name.trim(),
          title:
            typeof spokespersonRaw.title === 'string' && spokespersonRaw.title.trim()
              ? spokespersonRaw.title.trim()
              : undefined,
          quote:
            typeof spokespersonRaw.quote === 'string' && spokespersonRaw.quote.trim()
              ? spokespersonRaw.quote.trim()
              : undefined,
          updatedAt: toIsoString(spokespersonRaw.updatedAt),
        }
      : undefined;

  const mediaContact: MediaContactPreference | undefined =
    mediaRaw && typeof mediaRaw.name === 'string' && mediaRaw.name.trim()
      ? {
          name: mediaRaw.name.trim(),
          email:
            typeof mediaRaw.email === 'string' && mediaRaw.email.trim()
              ? mediaRaw.email.trim()
              : undefined,
          phone:
            typeof mediaRaw.phone === 'string' && mediaRaw.phone.trim()
              ? mediaRaw.phone.trim()
              : undefined,
          updatedAt: toIsoString(mediaRaw.updatedAt),
        }
      : undefined;

  return {
    version,
    ...(locale ? { locale } : {}),
    ...(spokesperson ? { spokesperson } : {}),
    ...(mediaContact ? { mediaContact } : {}),
    ...(tone ? { tone: tone as TonePreset } : {}),
    ...(styleNotes ? { styleNotes } : {}),
    ...(lengthPreference ? { lengthPreference } : {}),
    ...(structurePreference ? { structurePreference } : {}),
    ...(forbiddenTerms ? { forbiddenTerms } : {}),
    ...(requiredTerms ? { requiredTerms } : {}),
    ...(confidence !== undefined ? { confidence } : {}),
    ...(source ? { source } : {}),
    updatedAt: toIsoString(raw.updatedAt),
    updatedBy: typeof raw.updatedBy === 'string' ? raw.updatedBy : undefined,
  };
};

export async function fetchClientPreferences(clientId: string): Promise<ClientPreferences | null> {
  if (!clientId) return null;
  try {
    const prefRef = doc(db, 'clients', clientId, 'preferences', 'meta');
    const snap = await getDoc(prefRef);
    const prefs = snap.exists() ? normalizeClientPreferences(snap.data()) : null;
    trackPreferenceMetric('client_preferences_loaded_count', { clientId, hasPreferences: !!prefs });
    console.log('[preferences] loaded', { clientId, hasPreferences: !!prefs });
    return prefs;
  } catch (error) {
    console.warn('[preferences] load_failed', { clientId, error: (error as any)?.message || error });
    return null;
  }
}

export const mergeClientPreferences = (
  base: ClientPreferences | null,
  updates: Partial<ClientPreferences>,
  updatedBy?: string,
  locale?: 'en' | 'ar'
): ClientPreferences => {
  const now = new Date().toISOString();
  const nextVersion = typeof updates.version === 'number' ? updates.version : base?.version || 1;
  const nextLocale = updates.locale || locale || base?.locale;
  const nextTone = updates.tone || base?.tone;
  const nextStyleNotes = normalizeStyleNotes(updates.styleNotes) || base?.styleNotes;
  const nextLengthPreference = updates.lengthPreference || base?.lengthPreference;
  const nextStructurePreference = updates.structurePreference || base?.structurePreference;
  const nextForbiddenTerms = normalizeTerms(updates.forbiddenTerms) || base?.forbiddenTerms;
  const nextRequiredTerms = normalizeTerms(updates.requiredTerms) || base?.requiredTerms;
  const nextConfidence = typeof updates.confidence === 'number' ? updates.confidence : base?.confidence;
  const nextSource = updates.source || base?.source;

  const nextSpokesperson = updates.spokesperson
    ? {
        ...(base?.spokesperson || {}),
        ...updates.spokesperson,
        updatedAt: now,
      }
    : base?.spokesperson;

  const nextMediaContact = updates.mediaContact
    ? {
        ...(base?.mediaContact || {}),
        ...updates.mediaContact,
        updatedAt: now,
      }
    : base?.mediaContact;

  return {
    version: nextVersion,
    ...(nextLocale ? { locale: nextLocale } : {}),
    ...(nextSpokesperson ? { spokesperson: nextSpokesperson } : {}),
    ...(nextMediaContact ? { mediaContact: nextMediaContact } : {}),
    ...(nextTone ? { tone: nextTone } : {}),
    ...(nextStyleNotes ? { styleNotes: nextStyleNotes } : {}),
    ...(nextLengthPreference ? { lengthPreference: nextLengthPreference } : {}),
    ...(nextStructurePreference ? { structurePreference: nextStructurePreference } : {}),
    ...(nextForbiddenTerms ? { forbiddenTerms: nextForbiddenTerms } : {}),
    ...(nextRequiredTerms ? { requiredTerms: nextRequiredTerms } : {}),
    ...(nextConfidence !== undefined ? { confidence: nextConfidence } : {}),
    ...(nextSource ? { source: nextSource } : {}),
    updatedAt: now,
    ...(updatedBy ? { updatedBy } : {}),
  };
};

export async function saveClientPreferences(
  clientId: string,
  updates: Partial<ClientPreferences>,
  options?: { updatedBy?: string; current?: ClientPreferences | null; locale?: 'en' | 'ar' }
): Promise<ClientPreferences | null> {
  if (!clientId) return null;
  try {
    const current = options?.current ?? (await fetchClientPreferences(clientId));
    const merged = mergeClientPreferences(current, updates, options?.updatedBy, options?.locale);
    const prefRef = doc(db, 'clients', clientId, 'preferences', 'meta');
    await setDoc(prefRef, stripUndefined(merged), { merge: true });
    trackPreferenceMetric('client_preferences_saved_count', {
      clientId,
      fields: Object.keys(updates || {})
    });
    console.log('[preferences] saved', { clientId, fields: Object.keys(updates || {}) });
    return merged;
  } catch (error) {
    console.warn('[preferences] save_failed', { clientId, error: (error as any)?.message || error });
    return null;
  }
}

export async function saveUserClientExplicitPreferences(
  userId: string,
  clientId: string,
  updates: Partial<ClientPreferences>,
  options?: { current?: ClientPreferences | null; locale?: 'en' | 'ar' }
): Promise<ClientPreferences | null> {
  if (!userId || !clientId) return null;
  if (!updates || !Object.keys(updates).length) return null;
  try {
    const now = new Date().toISOString();
    const current = options?.current || null;
    const merged = mergeClientPreferences(current, updates, userId, options?.locale);
    const explicit: ClientPreferences = {
      ...merged,
      source: 'user_client_explicit',
      confidence: 1,
      updatedAt: now,
      updatedBy: userId,
    };

    const prefRef = doc(db, 'users', userId, 'clientPreferences', clientId);
    await setDoc(
      prefRef,
      stripUndefined({
        explicit,
        explicitUpdatedAt: now,
        updatedAt: now,
      }),
      { merge: true }
    );

    trackPreferenceMetric('user_client_preferences_saved_count', {
      clientId,
      userId,
      fields: Object.keys(updates || {}),
    });
    return explicit;
  } catch (error) {
    console.warn('[preferences] user_client_save_failed', {
      userId,
      clientId,
      error: (error as any)?.message || error,
    });
    return null;
  }
}

const EMAIL_REGEX = /[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/i;
const PHONE_REGEX = /(\+?\d[\d\s().-]{7,}\d)/;

const extractNamedLine = (line: string): { name?: string; title?: string } => {
  const trimmed = line.trim();
  if (!trimmed) return {};
  const [namePart, titlePart] = trimmed.split(',').map((part) => part.trim());
  if (!namePart) return {};
  return {
    name: namePart,
    ...(titlePart ? { title: titlePart } : {}),
  };
};

const extractMediaContact = (lines: string[]): MediaContactPreference | undefined => {
  const index = lines.findIndex((line) => /media contact|press contact/i.test(line));
  if (index < 0) return undefined;
  const line = lines[index];
  const email = line.match(EMAIL_REGEX)?.[0]
    || lines.slice(index + 1, index + 4).find((l) => EMAIL_REGEX.test(l))?.match(EMAIL_REGEX)?.[0];
  const phone = line.match(PHONE_REGEX)?.[1]
    || lines.slice(index + 1, index + 4).find((l) => PHONE_REGEX.test(l))?.match(PHONE_REGEX)?.[1];
  const nameLine = line.replace(/media contact[:\-\s]*/i, '').trim()
    || lines.slice(index + 1, index + 3).find((l) => l && !EMAIL_REGEX.test(l) && !PHONE_REGEX.test(l));
  const cleanedName = typeof nameLine === 'string' ? nameLine.trim() : '';
  const nameIsEmail = cleanedName ? EMAIL_REGEX.test(cleanedName) : false;
  const nameIsPhone = cleanedName ? PHONE_REGEX.test(cleanedName) : false;
  if ((!cleanedName || nameIsEmail || nameIsPhone) && !email && !phone) return undefined;
  return {
    name: cleanedName && !nameIsEmail && !nameIsPhone ? cleanedName : 'Press Contact',
    ...(email ? { email } : {}),
    ...(phone ? { phone } : {}),
    updatedAt: new Date().toISOString(),
  };
};

const extractSpokesperson = (lines: string[], text: string): SpokespersonPreference | undefined => {
  const patterns = [
    /\buse\s+(.+?)\s+as\s+(?:the\s+)?spokesperson\b/i,
    /\b(.+?)\s+is\s+(?:the\s+)?spokesperson\b/i,
    /\b(.+?)\s+will\s+be\s+(?:the\s+)?spokesperson\b/i,
  ];

  for (const line of lines) {
    for (const pattern of patterns) {
      const match = line.match(pattern);
      if (match && match[1]) {
        const candidate = match[1].replace(/[.!?]+$/, '').trim();
        const parsed = extractNamedLine(candidate);
        if (parsed.name) {
          return {
            name: parsed.name,
            ...(parsed.title ? { title: parsed.title } : {}),
            updatedAt: new Date().toISOString(),
          };
        }
      }
    }
  }

  const index = lines.findIndex((line) => /spokesperson/i.test(line));
  if (index >= 0) {
    const line = lines[index];
    const after = line.split(/spokesperson[:\-\s]*/i)[1]?.trim();
    const candidate = after || lines[index + 1] || '';
    const parsed = extractNamedLine(candidate);
    if (parsed.name) {
      return {
        name: parsed.name,
        ...(parsed.title ? { title: parsed.title } : {}),
        updatedAt: new Date().toISOString(),
      };
    }
  }

  const quoteMatch = text.match(/quote\s+from\s+([A-Z][a-z]+(?:\s+[A-Z][a-z]+){0,3})(?:,([^.\n]+))?/i);
  if (quoteMatch && quoteMatch[1]) {
    return {
      name: quoteMatch[1].trim(),
      ...(quoteMatch[2] ? { title: quoteMatch[2].trim() } : {}),
      updatedAt: new Date().toISOString(),
    };
  }
  return undefined;
};

const extractTone = (text: string): TonePreset | undefined => {
  const toneMatch = text.match(/\b(?:tone|voice)\s*(?:should be|should feel|is|:)\s*([a-z][a-z\s-]{2,40})/i);
  if (toneMatch && toneMatch[1]) {
    return toneMatch[1].trim();
  }
  const keywordMatch = text.match(/\b(optimistic|neutral|cautious)\b/i);
  if (keywordMatch) return keywordMatch[1].toLowerCase();
  return undefined;
};

const extractStyleNotes = (lines: string[], text: string): string[] | undefined => {
  const notes: string[] = [];
  if (/ap style/i.test(text)) {
    notes.push('Use AP style.');
  }
  const styleIndex = lines.findIndex((line) => /style notes|style guide|formatting|house style/i.test(line));
  if (styleIndex >= 0) {
    lines.slice(styleIndex + 1, styleIndex + 6).forEach((line) => {
      const cleaned = line.replace(/^[-*]\s*/, '').trim();
      if (cleaned) notes.push(cleaned);
    });
  }
  if (notes.length === 0 && /style notes|style guide|formatting|ap style/i.test(text)) {
    lines.filter((line) => /^[-*]\s+/.test(line)).slice(0, 5).forEach((line) => {
      const cleaned = line.replace(/^[-*]\s*/, '').trim();
      if (cleaned) notes.push(cleaned);
    });
  }
  return normalizeStyleNotes(notes);
};

export function extractPreferenceUpdates(
  text: string,
  locale?: 'en' | 'ar'
): Partial<ClientPreferences> {
  if (!text || !text.trim()) return {};
  const cleaned = text.replace(/\r/g, '\n');
  const lines = cleaned.split('\n').map((line) => line.trim()).filter(Boolean);
  const spokesperson = extractSpokesperson(lines, cleaned);
  const mediaContact = extractMediaContact(lines);
  const tone = extractTone(cleaned);
  const styleNotes = extractStyleNotes(lines, cleaned);
  const lengthPreference = /\b(short|concise|brief)\b/i.test(cleaned)
    ? 'short'
    : /\b(long|detailed|expanded)\b/i.test(cleaned)
      ? 'long'
      : undefined;
  const structurePreference = /\b(bullets?|bullet points?|list format)\b/i.test(cleaned)
    ? 'bulleted'
    : /\b(narrative|paragraph)\b/i.test(cleaned)
      ? 'narrative'
      : undefined;
  const detectedLocale = locale || (/[\u0600-\u06FF]/.test(cleaned) ? 'ar' : undefined);

  const updates: Partial<ClientPreferences> = {};
  if (spokesperson) updates.spokesperson = spokesperson;
  if (mediaContact) updates.mediaContact = mediaContact;
  if (tone) updates.tone = tone;
  if (styleNotes && styleNotes.length) updates.styleNotes = styleNotes;
  if (lengthPreference) updates.lengthPreference = lengthPreference;
  if (structurePreference) updates.structurePreference = structurePreference;
  if (detectedLocale) updates.locale = detectedLocale;

  return Object.keys(updates).length > 0 ? updates : {};
}
